import React from "react";
import { Button } from "@/components/ui/button";
import { TopNavbar } from "./TopNavbar";
import { Footer } from "./Footer";
import { AlertTriangle } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";

interface AppErrorBoundaryProps {
  children: React.ReactNode;
  key?: React.Key;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

function ErrorFallback({ error, onRetry }: { error: Error | null; onRetry: () => void }) {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  const homeHref = `/${lang || "en"}`;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-secondary/20 to-accent/20">
      <TopNavbar />
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-lg rounded-xl border bg-card text-card-foreground shadow-sm p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600">
            <AlertTriangle className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-semibold mb-2">Something went wrong</h1>
          <p className="text-muted-foreground mb-6">
            An unexpected error occurred. You can try again or go back to the home page.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button onClick={onRetry} variant="default">Try again</Button>
            <Button onClick={() => navigate(homeHref)} variant="outline">Go home</Button>
          </div>
          {error?.message && (
            <div className="mt-6 text-xs text-muted-foreground break-words text-left bg-muted/40 rounded-md p-3">
              <div className="font-medium mb-1">Error details</div>
              <div className="opacity-90">{error.message}</div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(error, {
        tags: { component: "AppErrorBoundary" },
        extra: { errorInfo },
      });
    } else {
      // eslint-disable-next-line no-console
      console.error("AppErrorBoundary caught an error:", error, errorInfo);
    }
  }

  handleRetry = () => {
    // Reset error state and re-render children.
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} onRetry={this.handleRetry} />;
    }
    return this.props.children;
  }
} 